/**
 * AdminAccommodationOps — campus accommodation beyond the room list.
 *
 * Three things the housing office has never had a screen for:
 *   • Event rooms   — reserve accommodation for a conference, see exactly who
 *                     that collides with, and give each of them a date. No
 *                     booking is cancelled here; notices are raised and a
 *                     human settles each one.
 *   • Conduct       — incidents, the warning ladder, and the management review
 *                     a third substantiated breach refers to. Nobody is
 *                     evicted by this screen.
 *   • Restrictions  — refusing somebody accommodation across the property
 *                     network, proposed by one officer and authorised by
 *                     another, always with a review date.
 *   • Food permits  — who may actually sell food from campus, campus by campus.
 *
 * Route: /admin/accommodation-ops
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { PersonPicker } from '@/components/people/PersonPicker';
import { getToken } from '@/lib/authToken';
import { CalendarClock, ShieldAlert, Ban, UtensilsCrossed, Loader2, AlertTriangle, Sunrise, ClipboardList } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, options = {}) => {
  const res = await axios({
    url: `${API_URL}/api${path}`,
    method: options.method || 'GET',
    data: options.body ? JSON.parse(options.body) : undefined,
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  return res.data;
};

const fail = (e) => toast.error(e.response?.data?.detail || e.message);

// ==================== EVENT ROOMS ====================

const NoticeRow = ({ notice, rooms, excludeRoomIds = [], onDone }) => {
  const [altRoom, setAltRoom] = useState('');
  const [reason, setReason] = useState('');
  const settled = ['vacated', 'inspected', 'exempt'].includes(notice.status);
  const act = useMutation({
    mutationFn: ({ path, body }) => api(`/accommodation/vacate-notices/${notice.id}/${path}`, {
      method: 'POST', body: JSON.stringify(body || {}),
    }),
    onSuccess: () => { toast.success('Recorded'); onDone?.(); },
    onError: fail,
  });

  return (
    <div className="border border-stone-200 rounded-lg p-4 space-y-3" data-testid={`vacate-notice-${notice.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium text-stone-900">{notice.guest_name || 'Occupant'} · Room {notice.room_number || '—'}</p>
          <p className="text-sm text-stone-600">
            {notice.occupant_category?.replace('_', ' ')} · must be clear by {String(notice.vacate_by).slice(0, 10)} at 10:00 ·
            {' '}occupancy resumes {notice.resume_from}
          </p>
          {!notice.guest_email && (
            <p className="text-sm text-amber-700">No email address on file — this notice has to be delivered by hand.</p>
          )}
        </div>
        <Badge variant={notice.protected ? 'destructive' : 'secondary'} data-testid={`notice-status-${notice.id}`}>
          {notice.protected ? 'Cannot be asked yet' : notice.status}
        </Badge>
      </div>

      {notice.protected && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
          <p className="font-medium flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> This occupant cannot lawfully be asked to leave on these dates.</p>
          <ul className="list-disc ml-5 mt-1 space-y-0.5">
            {(notice.blockers || []).map((b, i) => <li key={i}>{b}</li>)}
          </ul>
          <p className="mt-2">Offer another room, or stand this room down and let the event lose the bed.</p>
        </div>
      )}

      {settled ? (
        <p className="text-sm text-stone-600" data-testid={`notice-settled-${notice.id}`}>
          Settled: {notice.status === 'exempt'
            ? `stood down — ${notice.exempt_reason}`
            : `room ${notice.status}${notice.alternative_room_number ? `, resident moved to ${notice.alternative_room_number}` : ''}`}
        </p>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="flex gap-2">
              <Select value={altRoom} onValueChange={setAltRoom}>
                <SelectTrigger className="w-full" data-testid={`alt-room-${notice.id}`}><SelectValue placeholder="Offer another room" /></SelectTrigger>
                <SelectContent>
                  {(rooms || [])
                    .filter((r) => {
                      const id = String(r.id || r._id);
                      return id !== String(notice.room_id) && !excludeRoomIds.includes(id);
                    })
                    .slice(0, 60).map((r) => (
                      <SelectItem key={r.id || r._id} value={String(r.id || r._id)}>
                        {r.room_number} {r.building_name ? `· ${r.building_name}` : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Button variant="outline" disabled={!altRoom || act.isPending}
                onClick={() => act.mutate({ path: 'alternative', body: { room_id: altRoom } })}
                data-testid={`offer-alt-${notice.id}`}>Offer</Button>
            </div>
            <div className="flex gap-2">
              <Input placeholder="Reason, if standing the room down" value={reason}
                onChange={(e) => setReason(e.target.value)} data-testid={`exempt-reason-${notice.id}`} />
              <Button variant="outline" disabled={!reason.trim() || act.isPending}
                onClick={() => act.mutate({ path: 'exempt', body: { reason } })}
                data-testid={`exempt-${notice.id}`}>Stand down</Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="ghost" onClick={() => act.mutate({ path: 'acknowledge', body: {} })}
              data-testid={`ack-${notice.id}`}>Resident acknowledged</Button>
            <Button size="sm" variant="ghost" disabled={notice.protected}
              onClick={() => act.mutate({ path: 'vacated', body: { inspected: false } })}
              data-testid={`vacated-${notice.id}`}>Room vacated</Button>
            <Button size="sm" disabled={notice.protected}
              onClick={() => act.mutate({ path: 'vacated', body: { inspected: true } })}
              data-testid={`inspected-${notice.id}`}>Vacated &amp; inspected</Button>
          </div>
        </>
      )}
    </div>
  );
};

/** Who in these rooms has event priority in their agreement — and who has not.
 *  Walked one occupant at a time: a list of eighteen is a list nobody works
 *  through, and each of these is a conversation with a person. */
const AgreementTerms = ({ buildingId }) => {
  const qc = useQueryClient();
  const [at, setAt] = useState(0);
  const { data } = useQuery({
    queryKey: ['acc-agreements', buildingId],
    queryFn: () => api(`/accommodation/bookings/agreement-status?building_id=${buildingId || ''}`),
  });
  const record = useMutation({
    mutationFn: ({ bookingId, accepted }) => api(`/accommodation/bookings/${bookingId}/event-priority-term`, {
      method: 'POST', body: JSON.stringify({ accepted }),
    }),
    onSuccess: (_d, vars) => {
      toast.success(vars.accepted ? 'Recorded on the agreement' : 'Recorded as declined');
      qc.invalidateQueries({ queryKey: ['acc-agreements'] });
    },
    onError: fail,
  });

  const missing = (data?.bookings || []).filter((b) => !b.term_recorded);
  const declined = (data?.bookings || []).filter((b) => b.term_declined).length;
  if (!(data?.bookings || []).length) return null;

  const current = missing[Math.min(at, Math.max(0, missing.length - 1))];
  const total = (data?.bookings || []).length;

  return (
    <Card data-testid="agreement-terms">
      <CardHeader>
        <CardTitle className="text-lg">Event priority in the accommodation agreement</CardTitle>
        <CardDescription>
          A resident can only be asked to move for an event if their agreement said so before they
          moved in. {missing.length > 0
            ? `${missing.length} of ${total} current occupant(s) have not been asked. Work through them one at a time.`
            : 'Everybody has been asked.'}
          {declined > 0 && ` ${declined} declined — they stay where they are, and no conference can move them.`}
        </CardDescription>
      </CardHeader>
      {current && (
        <CardContent className="space-y-4">
          <div className="border border-stone-200 rounded-lg p-5 space-y-2" data-testid={`agreement-${current.booking_id}`}>
            <p className="text-xs uppercase tracking-wide text-stone-500">
              {Math.min(at + 1, missing.length)} of {missing.length}
            </p>
            <p className="text-lg font-medium text-stone-900">{current.occupant}</p>
            <p className="text-sm text-stone-600">
              Room {current.room_number || '—'} · {current.occupant_category?.replace('_', ' ')} ·
              {' '}owed {current.notice_days_owed} days' notice · here until {current.check_out_date || 'further notice'}
            </p>
            {!current.has_contact && (
              <p className="text-sm text-amber-700">No email on file — this one has to be asked in person.</p>
            )}
            <p className="text-sm text-stone-700 pt-2">
              Ask them to accept that campus rooms are sometimes needed for conferences, and that they
              may be asked to move with proper notice and an alternative room where one is available.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={record.isPending}
              onClick={() => record.mutate({ bookingId: current.booking_id, accepted: true })}
              data-testid={`record-term-${current.booking_id}`}>They accepted</Button>
            <Button size="sm" variant="outline" disabled={record.isPending}
              onClick={() => record.mutate({ bookingId: current.booking_id, accepted: false })}
              data-testid={`decline-term-${current.booking_id}`}>They declined</Button>
            <Button size="sm" variant="ghost" onClick={() => setAt(at + 1)}
              disabled={at + 1 >= missing.length} data-testid="agreement-skip">
              Not now — next occupant
            </Button>
            {at > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setAt(at - 1)}
                data-testid="agreement-back">Back</Button>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const EventRooms = () => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ event_name: '', from_date: '', to_date: '', prep_days: 1, cleaning_days: 1, building_ids: [] });
  const [open, setOpen] = useState(null);
  const [preview, setPreview] = useState(null);

  const { data: list, isLoading } = useQuery({ queryKey: ['acc-reservations'], queryFn: () => api('/accommodation/event-reservations') });
  const { data: dash } = useQuery({ queryKey: ['acc-res-dash'], queryFn: () => api('/accommodation/event-reservations/dashboard') });
  const { data: buildings } = useQuery({ queryKey: ['acc-buildings'], queryFn: () => api('/admin/accommodation/buildings') });
  const { data: rooms } = useQuery({ queryKey: ['acc-rooms'], queryFn: () => api('/admin/accommodation/rooms') });
  const { data: notices, refetch: refetchNotices } = useQuery({
    queryKey: ['acc-notices', open],
    queryFn: () => api(`/accommodation/event-reservations/${open}/notices`),
    enabled: !!open,
  });

  const buildingList = buildings?.buildings || buildings || [];
  const roomList = rooms?.rooms || rooms || [];

  const run = useMutation({
    mutationFn: ({ path, body, method }) => api(path, { method: method || 'POST', body: JSON.stringify(body || {}) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['acc-reservations'] }); qc.invalidateQueries({ queryKey: ['acc-res-dash'] }); refetchNotices(); },
    onError: fail,
  });

  const checkConflicts = async () => {
    try {
      setPreview(await api('/accommodation/event-reservations/preview', {
        method: 'POST', body: JSON.stringify({ ...form, room_ids: [] }),
      }));
    } catch (e) { fail(e); }
  };

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-4 gap-3">
        {[['Occupants to relocate', dash?.occupants_to_relocate], ['Awaiting a lawful alternative', dash?.awaiting_lawful_alternative],
          ['Rooms cleared', dash?.rooms_cleared], ['Reservations live', dash?.reservations?.length]].map(([label, value]) => (
          <Card key={label}><CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wide text-stone-500">{label}</p>
            <p className="text-2xl font-semibold text-stone-900">{value ?? '—'}</p>
          </CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Reserve accommodation for an event</CardTitle>
          <CardDescription>
            Check who it collides with before you commit to it. Creating a reservation cancels nobody's booking —
            it raises a notice for each affected occupant, and each one has to be settled by a person.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            <div><Label>Event</Label><Input value={form.event_name} data-testid="event-name"
              onChange={(e) => setForm({ ...form, event_name: e.target.value })} placeholder="Faith Heights Conference" /></div>
            <div><Label>Rooms needed from</Label><Input type="date" value={form.from_date} data-testid="event-from"
              onChange={(e) => setForm({ ...form, from_date: e.target.value })} /></div>
            <div><Label>Until</Label><Input type="date" value={form.to_date} data-testid="event-to"
              onChange={(e) => setForm({ ...form, to_date: e.target.value })} /></div>
            <div><Label>Days to prepare beforehand</Label><Input type="number" min="0" value={form.prep_days} data-testid="prep-days"
              onChange={(e) => setForm({ ...form, prep_days: Number(e.target.value) })} /></div>
            <div><Label>Days to clean afterwards</Label><Input type="number" min="0" value={form.cleaning_days} data-testid="cleaning-days"
              onChange={(e) => setForm({ ...form, cleaning_days: Number(e.target.value) })} /></div>
            <div>
              <Label>Building</Label>
              <Select value={form.building_ids[0] || ''} onValueChange={(v) => setForm({ ...form, building_ids: [v] })}>
                <SelectTrigger data-testid="event-building"><SelectValue placeholder="Choose a building" /></SelectTrigger>
                <SelectContent>
                  {buildingList.map((b) => <SelectItem key={b.id || b._id} value={String(b.id || b._id)}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {preview && (
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 text-sm space-y-2" data-testid="conflict-preview">
              <p className="text-stone-700">
                Rooms unavailable <strong>{preview.prepare_from}</strong> to <strong>{preview.resume_from}</strong> ·
                {' '}{preview.rooms_in_scope} room(s) · <strong>{preview.occupants_affected}</strong> occupant(s) affected ·
                {' '}<strong>{preview.protected_occupants}</strong> who cannot be asked to move on these dates
              </p>
              {(preview.occupants || []).slice(0, 8).map((o) => (
                <p key={o.booking_id} className={o.protected ? 'text-amber-800' : 'text-stone-600'}>
                  {o.guest_name || 'Occupant'} · {o.occupant_category} · owed {o.notice_days_owed} days' notice,
                  {' '}{o.notice_days_available} available{o.protected ? ' · blocked' : ''}
                </p>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={checkConflicts}
              disabled={!form.from_date || !form.to_date || !form.building_ids.length} data-testid="check-conflicts">
              Who does this affect?
            </Button>
            <Button onClick={() => run.mutate({ path: '/accommodation/event-reservations', body: form })}
              disabled={!form.event_name || !form.from_date || !form.to_date || !form.building_ids.length || run.isPending}
              data-testid="create-reservation">
              {run.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reserve these rooms'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <AgreementTerms buildingId={form.building_ids[0] || ''} />

      <Card>
        <CardHeader><CardTitle className="text-lg">Event reservations</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {isLoading && <Loader2 className="h-5 w-5 animate-spin text-stone-400" />}
          {(list?.reservations || []).map((r) => (
            <div key={r.id} className="border border-stone-200 rounded-lg p-4" data-testid={`reservation-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-stone-900">{r.event_name}</p>
                  <p className="text-sm text-stone-600">
                    {r.from_date} → {r.to_date} · rooms held {r.prepare_from} → {r.resume_from} ·
                    {' '}{r.rooms_requested} room(s) · {r.notices_total} notice(s)
                    {r.blocked_count ? ` · ${r.blocked_count} blocked` : ''}
                  </p>
                </div>
                <Badge variant={r.status === 'active' ? 'default' : 'secondary'}>{r.status}</Badge>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => setOpen(open === r.id ? null : r.id)}
                  data-testid={`view-notices-${r.id}`}>{open === r.id ? 'Hide notices' : 'Notices'}</Button>
                <Button size="sm" variant="outline"
                  onClick={() => run.mutate({ path: `/accommodation/event-reservations/${r.id}/send-notices` })}
                  data-testid={`send-notices-${r.id}`}>Send notices</Button>
                <Button size="sm"
                  onClick={() => run.mutate({ path: `/accommodation/event-reservations/${r.id}/activate` })}
                  data-testid={`activate-${r.id}`}>Rooms are ready</Button>
              </div>
              {open === r.id && (
                <div className="mt-4 space-y-3">
                  {(notices?.notices || []).length === 0 && <p className="text-sm text-stone-500">Nobody is affected by this reservation.</p>}
                  {(notices?.notices || []).map((n) => (
                    <NoticeRow key={n.id} notice={n} rooms={roomList} excludeRoomIds={r.room_ids || []}
                      onDone={() => { refetchNotices(); qc.invalidateQueries({ queryKey: ['acc-res-dash'] }); }} />
                  ))}
                </div>
              )}
            </div>
          ))}
          {!isLoading && !(list?.reservations || []).length && (
            <p className="text-sm text-stone-500">No event accommodation has been reserved yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== CONDUCT ====================

const IncidentCard = ({ incident, onDone }) => {
  const [findings, setFindings] = useState('');
  const [reasons, setReasons] = useState('');
  const act = useMutation({
    mutationFn: ({ path, body }) => api(`/accommodation/conduct/incidents/${incident.id}/${path}`, {
      method: 'POST', body: JSON.stringify(body),
    }),
    onSuccess: (d) => { toast.success(d.next_step || 'Recorded'); onDone?.(); },
    onError: fail,
  });

  return (
    <div className="border border-stone-200 rounded-lg p-4 space-y-3" data-testid={`incident-${incident.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium text-stone-900">{incident.person_name || incident.person_key}</p>
          <p className="text-sm text-stone-600">{incident.rule_code?.replace('_', ' ')} · {incident.occurred_on}</p>
          <p className="text-sm text-stone-700 mt-1">{incident.description}</p>
        </div>
        <div className="flex gap-2">
          {incident.severity === 'serious' && <Badge variant="destructive">Serious</Badge>}
          <Badge variant="secondary">{incident.warning_label || incident.status}</Badge>
        </div>
      </div>

      {incident.status === 'reported' && (
        <div className="space-y-2">
          <Textarea placeholder="What was found, and on what evidence" value={findings}
            onChange={(e) => setFindings(e.target.value)} data-testid={`findings-${incident.id}`} />
          <div className="flex gap-2">
            <Button size="sm" disabled={!findings.trim() || act.isPending}
              onClick={() => act.mutate({ path: 'substantiate', body: { findings } })}
              data-testid={`substantiate-${incident.id}`}>Uphold this</Button>
            <Button size="sm" variant="outline" disabled={!findings.trim() || act.isPending}
              onClick={() => act.mutate({ path: 'dismiss', body: { reason: findings } })}
              data-testid={`dismiss-${incident.id}`}>Not upheld</Button>
          </div>
        </div>
      )}

      {incident.requires_management_review && (
        <div className="bg-red-50 border border-red-200 rounded p-3 space-y-2">
          <p className="text-sm text-red-900 font-medium">
            Referred for management review. Nobody has been removed — that is this decision, under the resident's agreement.
          </p>
          <Textarea placeholder="Reasons for the decision" value={reasons}
            onChange={(e) => setReasons(e.target.value)} data-testid={`review-reasons-${incident.id}`} />
          <div className="flex flex-wrap gap-2">
            {[['no_action', 'No further action'], ['conditions', 'Conditions imposed'], ['terminate', 'Terminate accommodation']].map(([value, label]) => (
              <Button key={value} size="sm" variant={value === 'terminate' ? 'destructive' : 'outline'}
                disabled={!reasons.trim() || act.isPending}
                onClick={() => act.mutate({ path: 'review', body: { decision: value, reasons, notice_days: value === 'terminate' ? 28 : 0 } })}
                data-testid={`review-${value}-${incident.id}`}>{label}</Button>
            ))}
          </div>
        </div>
      )}

      {incident.review && (
        <p className="text-sm text-stone-600">
          Reviewed: <strong>{incident.review.decision}</strong> by {incident.review.decided_by}
          {incident.review.notice_days ? ` · ${incident.review.notice_days} days' notice` : ''}
          {incident.review.appeal?.status === 'open' ? ' · appeal open' : ''}
        </p>
      )}
    </div>
  );
};

const Conduct = () => {
  const qc = useQueryClient();
  const [person, setPerson] = useState(null);
  const [form, setForm] = useState({ rule_code: 'respect', description: '', severity: 'minor', campus_id: '' });

  const { data: queue, isLoading } = useQuery({ queryKey: ['conduct-queue'], queryFn: () => api('/accommodation/conduct/queue') });
  const { data: rules } = useQuery({ queryKey: ['conduct-rules'], queryFn: () => api('/accommodation/conduct/rules') });
  const { data: profile } = useQuery({
    queryKey: ['conduct-profile', person?.identity_key],
    queryFn: () => api(`/accommodation/conduct/profile/${encodeURIComponent(person.identity_key)}`),
    enabled: !!person,
  });

  const report = useMutation({
    mutationFn: () => api('/accommodation/conduct/incidents', {
      method: 'POST',
      body: JSON.stringify({ ...form, person_key: person.identity_key, person_name: person.name }),
    }),
    onSuccess: () => {
      toast.success('Incident recorded', { description: 'An allegation is not a finding — it carries no warning until it is upheld.' });
      setForm({ ...form, description: '' });
      qc.invalidateQueries({ queryKey: ['conduct-queue'] });
      qc.invalidateQueries({ queryKey: ['conduct-profile'] });
    },
    onError: fail,
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: ['conduct-queue'] }); qc.invalidateQueries({ queryKey: ['conduct-profile'] }); };

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-4 gap-3">
        {[['Awaiting a decision', queue?.awaiting_decision?.length], ['Awaiting management review', queue?.awaiting_management_review?.length],
          ['Serious, unresolved', queue?.serious_unresolved], ['Restrictions due review', queue?.restrictions_due_review]].map(([label, value]) => (
          <Card key={label}><CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wide text-stone-500">{label}</p>
            <p className="text-2xl font-semibold text-stone-900">{value ?? '—'}</p>
          </CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Record an incident</CardTitle>
          <CardDescription>
            Find the resident in the directory rather than typing them again. Recording this issues no warning —
            a warning is placed only when an officer upholds the breach.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <PersonPicker selected={person} onSelect={setPerson} onClear={() => setPerson(null)} label="Resident" />
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Which rule</Label>
              <Select value={form.rule_code} onValueChange={(v) => setForm({ ...form, rule_code: v })}>
                <SelectTrigger data-testid="rule-code"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(rules?.default_codes || []).map((r) => <SelectItem key={r.code} value={r.code}>{r.text}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>How serious</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="minor">Ordinary breach — goes on the ladder</SelectItem>
                  <SelectItem value="serious">Serious — straight to review</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Textarea placeholder="What happened" value={form.description} data-testid="incident-description"
            onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Button disabled={!person || !form.description.trim() || report.isPending}
            onClick={() => report.mutate()} data-testid="report-incident">Record it</Button>
        </CardContent>
      </Card>

      {profile && (
        <Card data-testid="conduct-profile">
          <CardHeader>
            <CardTitle className="text-lg">{profile.person_name || person?.name} — conduct record</CardTitle>
            <CardDescription>{profile.substantiated} breach(es) upheld · currently: {profile.stage}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(profile.restrictions || []).map((r) => (
              <div key={r.id} className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-900">
                Accommodation restriction in force · {r.scope} · review due {r.review_date}
              </div>
            ))}
            {(profile.incidents || []).map((i) => <IncidentCard key={i.id} incident={i} onDone={refresh} />)}
            {!(profile.incidents || []).length && <p className="text-sm text-stone-500">Nothing on record.</p>}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-lg">Waiting on the housing office</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {isLoading && <Loader2 className="h-5 w-5 animate-spin text-stone-400" />}
          {[...(queue?.awaiting_management_review || []), ...(queue?.awaiting_decision || [])].map((i) => (
            <IncidentCard key={i.id} incident={i} onDone={refresh} />
          ))}
          {!isLoading && !(queue?.awaiting_decision || []).length && !(queue?.awaiting_management_review || []).length && (
            <p className="text-sm text-stone-500">Nothing waiting.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== RESTRICTIONS ====================

const Restrictions = () => {
  const qc = useQueryClient();
  const [person, setPerson] = useState(null);
  const [form, setForm] = useState({ scope: 'campus', reason: '', review_months: 12, campus_id: '', property_ids: [] });
  const [status, setStatus] = useState('active');
  const [grounds, setGrounds] = useState({});

  const { data } = useQuery({ queryKey: ['restrictions', status], queryFn: () => api(`/accommodation/restrictions?status=${status}`) });
  const { data: buildings } = useQuery({ queryKey: ['acc-buildings'], queryFn: () => api('/admin/accommodation/buildings') });
  const { data: screen } = useQuery({
    queryKey: ['restriction-screen', person?.identity_key, form.campus_id, form.property_ids[0]],
    queryFn: () => api(`/accommodation/restrictions/screen?person_key=${encodeURIComponent(person.identity_key)}`
      + `&campus_id=${encodeURIComponent(form.campus_id || '')}&building_id=${encodeURIComponent(form.property_ids[0] || '')}`),
    enabled: !!person,
  });

  const buildingList = buildings?.buildings || buildings || [];
  const needsBuilding = form.scope === 'building';
  const needsCampus = ['campus', 'institution'].includes(form.scope);
  const scopedOk = form.scope === 'network'
    || (needsBuilding && form.property_ids.length)
    || (needsCampus && form.campus_id.trim());

  const run = useMutation({
    mutationFn: ({ path, body }) => api(path, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      toast.success('Recorded');
      qc.invalidateQueries({ queryKey: ['restrictions'] });
      qc.invalidateQueries({ queryKey: ['restriction-screen'] });
    },
    onError: fail,
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Propose an accommodation restriction</CardTitle>
          <CardDescription>
            Proposed, not applied — a second officer has to authorise it, and it always carries a review date.
            At another campus it is shown to the housing officer as a flag, never as an automatic refusal.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <PersonPicker selected={person} onSelect={setPerson} onClear={() => setPerson(null)} label="Person" />
          {screen && (
            <div className={`rounded p-3 text-sm ${screen.clear ? 'bg-stone-50 text-stone-700' : 'bg-amber-50 text-amber-900 border border-amber-200'}`}
              data-testid="screen-result">
              {screen.note}
              {screen.unauthorised_proposals > 0 && ' There is also an unauthorised proposal on file, which restricts nothing.'}
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <Label>How widely</Label>
              <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                <SelectTrigger data-testid="restriction-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="building">This building</SelectItem>
                  <SelectItem value="campus">This campus</SelectItem>
                  <SelectItem value="institution">This institution</SelectItem>
                  <SelectItem value="network">Every FWM property</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {needsBuilding && (
              <div>
                <Label>Which building</Label>
                <Select value={form.property_ids[0] || ''} onValueChange={(v) => setForm({ ...form, property_ids: [v] })}>
                  <SelectTrigger data-testid="restriction-building"><SelectValue placeholder="Choose a building" /></SelectTrigger>
                  <SelectContent>
                    {buildingList.map((b) => <SelectItem key={b.id || b._id} value={String(b.id || b._id)}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {needsCampus && (
              <div>
                <Label>Which {form.scope}</Label>
                <Input value={form.campus_id} data-testid="restriction-campus" placeholder="Harare"
                  onChange={(e) => setForm({ ...form, campus_id: e.target.value })} />
              </div>
            )}
            <div><Label>Review in (months)</Label><Input type="number" min="1" max="60" value={form.review_months} data-testid="review-months"
              onChange={(e) => setForm({ ...form, review_months: Number(e.target.value) })} /></div>
          </div>
          {!scopedOk && (
            <p className="text-sm text-amber-700">
              Name the {needsBuilding ? 'building' : form.scope} this applies to — a restriction with nothing to scope to reaches nowhere.
            </p>
          )}
          <Textarea placeholder="The grounds — what happened, and the decisions it rests on" value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="restriction-reason" />
          <Button disabled={!person || !form.reason.trim() || !scopedOk || run.isPending} data-testid="propose-restriction"
            onClick={() => run.mutate({ path: '/accommodation/restrictions', body: { ...form, person_key: person.identity_key, person_name: person.name } })}>
            Propose it
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Restrictions</CardTitle>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="restriction-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="proposed">Proposed</SelectItem>
              <SelectItem value="active">In force</SelectItem>
              <SelectItem value="lifted">Lifted</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="space-y-3">
          {(data?.restrictions || []).map((r) => (
            <div key={r.id} className="border border-stone-200 rounded-lg p-4 space-y-2" data-testid={`restriction-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-stone-900">{r.person_name || r.person_key}</p>
                  <p className="text-sm text-stone-600">{r.scope} · proposed by {r.proposed_by}{r.review_date ? ` · review ${r.review_date}` : ''}</p>
                  <p className="text-sm text-stone-700 mt-1">{r.reason}</p>
                </div>
                <Badge variant={r.status === 'active' ? 'destructive' : 'secondary'}>{r.status}</Badge>
              </div>
              {r.status === 'proposed' && (
                <div className="flex gap-2">
                  <Input placeholder="Your grounds for authorising it" value={grounds[r.id] || ''}
                    onChange={(e) => setGrounds({ ...grounds, [r.id]: e.target.value })} data-testid={`grounds-${r.id}`} />
                  <Button size="sm" onClick={() => run.mutate({ path: `/accommodation/restrictions/${r.id}/authorise`, body: { grounds: grounds[r.id] || '' } })}
                    data-testid={`authorise-${r.id}`}>Authorise</Button>
                </div>
              )}
              {r.status === 'active' && (
                <div className="flex gap-2">
                  <Input placeholder="Reason for lifting it" value={grounds[r.id] || ''}
                    onChange={(e) => setGrounds({ ...grounds, [r.id]: e.target.value })} data-testid={`lift-reason-${r.id}`} />
                  <Button size="sm" variant="outline" disabled={!(grounds[r.id] || '').trim()}
                    onClick={() => run.mutate({ path: `/accommodation/restrictions/${r.id}/lift`, body: { reason: grounds[r.id] } })}
                    data-testid={`lift-${r.id}`}>Lift it</Button>
                </div>
              )}
            </div>
          ))}
          {!(data?.restrictions || []).length && <p className="text-sm text-stone-500">Nothing here.</p>}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== FOOD PERMITS ====================

const FoodPermits = () => {
  const qc = useQueryClient();
  const [person, setPerson] = useState(null);
  const [form, setForm] = useState({ campus_id: '', trading_name: '', description: '', hours: '' });
  const [revokeReason, setRevokeReason] = useState({});

  const { data } = useQuery({ queryKey: ['food-permits'], queryFn: () => api('/accommodation/food-permits') });
  const run = useMutation({
    mutationFn: ({ path, body }) => api(path, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => { toast.success('Recorded'); qc.invalidateQueries({ queryKey: ['food-permits'] }); },
    onError: fail,
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Apply to sell food from campus</CardTitle>
          <CardDescription>
            Cooking your own supper is a residence matter, not this. Selling food is a business: hygiene, a licence,
            an agreement and a named trading place, all of them before a permit is granted — and a permit at one campus
            is a permit at that campus only.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <PersonPicker selected={person} onSelect={setPerson} onClear={() => setPerson(null)} label="Applicant" />
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Trading name</Label><Input value={form.trading_name} data-testid="trading-name"
              onChange={(e) => setForm({ ...form, trading_name: e.target.value })} /></div>
            <div><Label>Campus</Label><Input value={form.campus_id} data-testid="permit-campus"
              onChange={(e) => setForm({ ...form, campus_id: e.target.value })} placeholder="Harare" /></div>
            <div><Label>Hours</Label><Input value={form.hours} data-testid="permit-hours"
              onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="Weekdays 17:00–20:00" /></div>
          </div>
          <Textarea placeholder="What they intend to sell, and from where" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="permit-description" />
          <Button disabled={!person || !form.trading_name.trim() || !form.campus_id.trim() || run.isPending}
            data-testid="request-permit"
            onClick={() => run.mutate({ path: '/accommodation/food-permits', body: { ...form, person_key: person.identity_key, person_name: person.name } })}>
            Start the application
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Permits</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {(data?.permits || []).map((p) => {
            const outstanding = Object.entries(p.requirements || {}).filter(([, met]) => !met);
            return (
              <div key={p.id} className="border border-stone-200 rounded-lg p-4 space-y-3" data-testid={`permit-${p.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-stone-900">{p.trading_name} · {p.person_name || p.person_key}</p>
                    <p className="text-sm text-stone-600">{p.campus_id}{p.hours ? ` · ${p.hours}` : ''}</p>
                  </div>
                  <Badge variant={p.status === 'granted' ? 'default' : 'secondary'}>{p.status}</Badge>
                </div>
                {p.status !== 'granted' && p.status !== 'revoked' && (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(p.requirements || {}).map(([key, met]) => (
                      <Button key={key} size="sm" variant={met ? 'default' : 'outline'}
                        onClick={() => run.mutate({ path: `/accommodation/food-permits/${p.id}/requirement`, body: { requirement: key, met: !met } })}
                        data-testid={`req-${key}-${p.id}`}>
                        {met ? '✓ ' : ''}{key.replace(/_/g, ' ')}
                      </Button>
                    ))}
                  </div>
                )}
                {p.status === 'granted' && (
                  <div className="flex gap-2">
                    <Input placeholder="Reason for revoking it" value={revokeReason[p.id] || ''}
                      onChange={(e) => setRevokeReason({ ...revokeReason, [p.id]: e.target.value })}
                      data-testid={`revoke-reason-${p.id}`} />
                    <Button size="sm" variant="outline" disabled={!(revokeReason[p.id] || '').trim()}
                      onClick={() => run.mutate({ path: `/accommodation/food-permits/${p.id}/revoke`, body: { reason: revokeReason[p.id] } })}
                      data-testid={`revoke-${p.id}`}>Revoke</Button>
                  </div>
                )}
                {p.status === 'revoked' && (
                  <p className="text-sm text-stone-600" data-testid={`revoked-note-${p.id}`}>
                    Revoked — {p.revoked_reason}. Trading again needs a fresh application, so the hygiene and licence
                    checks are met again rather than carried over.
                  </p>
                )}
                {p.status === 'requested' && (
                  <Button size="sm" disabled={outstanding.length > 0}
                    onClick={() => run.mutate({ path: `/accommodation/food-permits/${p.id}/grant`, body: {} })}
                    data-testid={`grant-${p.id}`}>
                    {outstanding.length ? `${outstanding.length} requirement(s) outstanding` : 'Grant the permit'}
                  </Button>
                )}
              </div>
            );
          })}
          {!(data?.permits || []).length && <p className="text-sm text-stone-500">No permit applications.</p>}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== APPLICATIONS ====================

const ApplicationCard = ({ id, rooms, onDone }) => {
  const [note, setNote] = useState('');
  const [applicantNote, setApplicantNote] = useState('');
  const [room, setRoom] = useState('');
  const [override, setOverride] = useState('');
  const { data: app, refetch } = useQuery({
    queryKey: ['application', id],
    queryFn: () => api(`/accommodation/applications/${id}`),
  });
  const act = useMutation({
    mutationFn: ({ path, body }) => api(`/accommodation/applications/${id}/${path}`, {
      method: 'POST', body: JSON.stringify(body),
    }),
    onSuccess: (d) => { toast.success(d.next_step || 'Recorded'); refetch(); onDone?.(); },
    onError: fail,
  });

  if (!app) return <Loader2 className="h-5 w-5 animate-spin text-stone-400" />;

  return (
    <div className="space-y-4" data-testid={`application-detail-${id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-lg font-medium text-stone-900">{app.full_name}</p>
          <p className="text-sm text-stone-600">
            {app.category_label} · {app.campus_id} · {app.arrival} → {app.departure} ({app.nights} night{app.nights === 1 ? '' : 's'}) ·
            {' '}ref {app.reference}
          </p>
        </div>
        <Badge variant={app.status === 'allocated' ? 'default' : 'secondary'}>{app.status}</Badge>
      </div>

      {app.screen && !app.screen.clear && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900" data-testid={`app-screen-${id}`}>
          <p className="font-medium flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Restriction flag</p>
          <p>{app.screen.note}</p>
        </div>
      )}

      {(app.person_candidates || []).length > 0 && !app.person_key && (
        <div className="bg-stone-50 border border-stone-200 rounded p-3 text-sm space-y-2" data-testid={`app-candidates-${id}`}>
          <p className="text-stone-700">We may already hold this person. Linking keeps one identity; it never merges records.</p>
          {app.person_candidates.map((c) => (
            <div key={c.identity_key} className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-stone-600">{c.name} · matched on {c.matched_on} · {(c.modules || []).join(', ')}</span>
              <Button size="sm" variant="outline"
                onClick={() => act.mutate({ path: 'link-person', body: { person_key: c.identity_key } })}
                data-testid={`link-${c.identity_key}`}>Same person</Button>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <p className="text-sm font-medium text-stone-900">
          What this stay requires ({app.outstanding_count} outstanding)
        </p>
        {(app.requirements || []).map((r) => (
          <div key={r.code} className="flex flex-wrap items-center justify-between gap-2 border border-stone-200 rounded p-3"
            data-testid={`app-req-${r.code}`}>
            <div>
              <p className="text-sm text-stone-900">{r.label}</p>
              <p className="text-xs text-stone-500">{r.why}</p>
              {r.waived && <p className="text-xs text-amber-700">Waived by {r.decided_by} — {r.reason}</p>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant={r.met ? 'default' : 'outline'}
                onClick={() => act.mutate({ path: 'requirement', body: { code: r.code, met: !r.met } })}
                data-testid={`req-met-${r.code}`}>{r.met ? '✓ In' : 'Mark in'}</Button>
              {!r.met && !r.waived && (
                <Button size="sm" variant="ghost" disabled={!note.trim()}
                  onClick={() => act.mutate({ path: 'requirement', body: { code: r.code, waived: true, reason: note } })}
                  data-testid={`req-waive-${r.code}`}>Waive</Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {(app.exclusions || []).length > 0 && (
        <div className="bg-stone-100 rounded p-3 space-y-1" data-testid={`app-exclusions-${id}`}>
          <p className="text-sm font-medium text-stone-900">Deliberately not asked for</p>
          {app.exclusions.map((n, i) => <p key={i} className="text-sm text-stone-600">{n}</p>)}
        </div>
      )}

      <Textarea placeholder="A note for the file — required to decline, or to waive a requirement" value={note}
        onChange={(e) => setNote(e.target.value)} data-testid={`app-note-${id}`} />
      <Input placeholder="What the applicant should be told (optional — they see only this)"
        value={applicantNote} onChange={(e) => setApplicantNote(e.target.value)}
        data-testid={`app-applicant-note-${id}`} />

      <div className="flex flex-wrap gap-2">
        {['submitted', 'in_review', 'waitlisted'].includes(app.status) && (
          <>
            <Button size="sm" onClick={() => act.mutate({ path: 'decision', body: { approve: true, note, applicant_note: applicantNote } })}
              data-testid={`approve-${id}`}>Approve</Button>
            <Button size="sm" variant="outline" disabled={!note.trim()}
              onClick={() => act.mutate({ path: 'decision', body: { approve: false, note, applicant_note: applicantNote } })}
              data-testid={`decline-${id}`}>Decline</Button>
            <Button size="sm" variant="ghost" onClick={() => act.mutate({ path: 'waitlist', body: { note } })}
              data-testid={`waitlist-${id}`}>Waiting list</Button>
          </>
        )}
      </div>

      {app.status === 'approved' && (
        <div className="border border-stone-200 rounded-lg p-4 space-y-3">
          <p className="text-sm text-stone-700">
            Approved is not a room. Allocating one is refused while documents or money are outstanding —
            name a reason if you mean to proceed anyway.
          </p>
          <div className="flex flex-wrap gap-2">
            <Select value={room} onValueChange={setRoom}>
              <SelectTrigger className="w-72" data-testid={`alloc-room-${id}`}><SelectValue placeholder="Choose a room" /></SelectTrigger>
              <SelectContent>
                {(rooms || [])
                  // Rooms somebody is already in, or that are out of service, are
                  // not offered — the allocation would be refused anyway, and
                  // offering them invites the clerk to try.
                  .filter((r) => !r.current_guest && !['maintenance', 'blocked'].includes(r.status))
                  .slice(0, 200).map((r) => (
                    <SelectItem key={r.id || r._id} value={String(r.id || r._id)}>
                      {r.room_number} · {r.building_name || ''} · {r.status}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Input className="w-72" placeholder="Reason, if proceeding with things outstanding"
              value={override} onChange={(e) => setOverride(e.target.value)}
              data-testid={`alloc-override-${id}`} />
            <Button size="sm" disabled={!room}
              onClick={() => act.mutate({ path: 'allocate', body: { room_id: room, override_reason: override } })}
              data-testid={`allocate-${id}`}>Allocate the room</Button>
          </div>
        </div>
      )}

      {app.status === 'allocated' && (
        <p className="text-sm text-stone-700" data-testid={`allocated-${id}`}>
          Room {app.room_number} allocated by {app.allocated_by}. Confirmed, not checked in —
          they will appear on the arrival board.
        </p>
      )}

      {['approved', 'allocated'].includes(app.status) && (
        <div className="border border-stone-200 rounded-lg p-4 space-y-2">
          <p className="text-sm text-stone-700">
            The welcome letter tells them their room, when to check in, and what they still need to
            bring — which is their own outstanding list, not a generic one.
          </p>
          <Button size="sm" variant="outline" disabled={act.isPending}
            onClick={() => act.mutate({ path: 'welcome', body: {} })}
            data-testid={`welcome-${id}`}>
            {app.welcome_sent_at ? 'Send it again' : 'Send the welcome letter'}
          </Button>
          {app.welcome_sent_at && (
            <p className="text-xs text-stone-500">Last sent {String(app.welcome_sent_at).slice(0, 10)}.</p>
          )}
        </div>
      )}

      <ApplicationDocuments id={id} />
    </div>
  );
};

/** What the applicant has sent in, and what becomes of it.
 *  Opening one is a five-minute signed link and a line in the access log. */
const ApplicationDocuments = ({ id }) => {
  const { data } = useQuery({
    queryKey: ['app-documents', id],
    queryFn: () => api(`/accommodation/applications/${id}/documents`),
  });
  const open = useMutation({
    mutationFn: (docId) => api(`/accommodation/documents/${docId}/open`, { method: 'POST', body: '{}' }),
    onSuccess: (d) => { window.open(d.url, '_blank', 'noopener'); },
    onError: fail,
  });
  const docs = data?.documents || [];
  if (!docs.length) return null;

  return (
    <div className="border border-stone-200 rounded-lg p-4 space-y-2" data-testid={`app-documents-${id}`}>
      <p className="text-sm font-medium text-stone-900">Documents on file</p>
      <p className="text-xs text-stone-500">
        A clearance is kept only as long as there is a reason to hold it, and every opening is logged.
      </p>
      {docs.map((d) => (
        <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 text-sm"
          data-testid={`document-${d.id}`}>
          <span className="text-stone-700">
            {d.requirement_code?.replace(/_/g, ' ')} · {d.filename}
            {d.deleted
              ? ` · withdrawn ${d.deleted_at}`
              : ` · kept until ${d.kept_until}${d.times_opened ? ` · opened ${d.times_opened}×` : ''}`}
          </span>
          {!d.deleted && (
            <Button size="sm" variant="ghost" onClick={() => open.mutate(d.id)}
              data-testid={`open-document-${d.id}`}>Open</Button>
          )}
        </div>
      ))}
    </div>
  );
};

const Applications = () => {
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ['applications', status],
    queryFn: () => api(`/accommodation/applications?status=${status}`),
  });
  const { data: rooms } = useQuery({ queryKey: ['acc-rooms'], queryFn: () => api('/admin/accommodation/rooms') });
  const roomList = rooms?.rooms || rooms || [];
  const s = data?.summary || {};

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-4 gap-3">
        {[['New', s.submitted], ['In review', s.in_review], ['Approved, no room yet', s.approved_awaiting_room],
          ['Arriving within a week', s.arriving_within_7_days]].map(([label, value]) => (
          <Card key={label}><CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wide text-stone-500">{label}</p>
            <p className="text-2xl font-semibold text-stone-900">{value ?? '—'}</p>
          </CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Room applications</CardTitle>
            <CardDescription>
              Each applicant is asked only for what the stay warrants — a guest is never asked for
              police clearance, and the shorter list is a decision, not an omission.
            </CardDescription>
          </div>
          <Select value={status || 'all'} onValueChange={(v) => setStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-44" data-testid="application-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everything</SelectItem>
              <SelectItem value="submitted">New</SelectItem>
              <SelectItem value="in_review">In review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="allocated">Allocated</SelectItem>
              <SelectItem value="waitlisted">Waiting list</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading && <Loader2 className="h-5 w-5 animate-spin text-stone-400" />}
          {(data?.applications || []).map((a) => (
            <div key={a.id} className="border border-stone-200 rounded-lg p-4" data-testid={`application-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-stone-900">{a.full_name} · {a.reference}</p>
                  <p className="text-sm text-stone-600">
                    {a.category_label} · {a.campus_id} · {a.arrival} → {a.departure} ·
                    {' '}{a.outstanding_count} outstanding
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={a.status === 'allocated' ? 'default' : 'secondary'}>{a.status}</Badge>
                  <Button size="sm" variant="outline" onClick={() => setOpen(open === a.id ? null : a.id)}
                    data-testid={`open-application-${a.id}`}>{open === a.id ? 'Close' : 'Open'}</Button>
                </div>
              </div>
              {open === a.id && (
                <div className="mt-4 pt-4 border-t border-stone-200">
                  <ApplicationCard id={a.id} rooms={roomList}
                    onDone={() => qc.invalidateQueries({ queryKey: ['applications'] })} />
                </div>
              )}
            </div>
          ))}
          {!isLoading && !(data?.applications || []).length && (
            <p className="text-sm text-stone-500">No applications{status ? ` with that status` : ''} yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/** A deposit at the two moments it matters: taken at arrival, settled at
 *  check-out. Held money, never counted as takings. */
const DepositPanel = ({ bookingId, mode }) => {
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const [deduct, setDeduct] = useState('');
  const [reason, setReason] = useState('');
  const { data } = useQuery({
    queryKey: ['deposit', bookingId],
    queryFn: () => api(`/accommodation/bookings/${bookingId}/deposit`),
  });
  const act = useMutation({
    mutationFn: ({ path, body }) => api(path, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (d) => {
      toast.success(d.note || 'Recorded');
      qc.invalidateQueries({ queryKey: ['deposit', bookingId] });
      qc.invalidateQueries({ queryKey: ['arrival-board'] });
    },
    onError: fail,
  });
  const d = data?.deposit;

  if (!d) {
    return mode === 'arrival' ? (
      <div className="flex gap-2" data-testid={`take-deposit-${bookingId}`}>
        <Input className="w-36" type="number" placeholder="Deposit" value={amount}
          onChange={(e) => setAmount(e.target.value)} data-testid={`deposit-amount-${bookingId}`} />
        <Button size="sm" variant="outline" disabled={!amount || Number(amount) <= 0}
          onClick={() => act.mutate({ path: `/accommodation/bookings/${bookingId}/deposit`,
            body: { amount: Number(amount) } })}
          data-testid={`deposit-take-${bookingId}`}>Take deposit</Button>
      </div>
    ) : <p className="text-sm text-stone-500">No deposit was taken.</p>;
  }

  return (
    <div className="space-y-2" data-testid={`deposit-${bookingId}`}>
      <p className="text-sm text-stone-700">
        Deposit {d.currency} {Number(d.amount).toFixed(2)} · <strong>{d.status}</strong>
        {d.status !== 'held' && ` · kept ${d.currency} ${Number(d.deducted_total).toFixed(2)}, back ${d.currency} ${Number(d.refund_due).toFixed(2)}`}
      </p>
      {d.status === 'held' && mode === 'departure' && (
        <div className="flex flex-wrap gap-2">
          <Input className="w-32" type="number" placeholder="Damage" value={deduct}
            onChange={(e) => setDeduct(e.target.value)} data-testid={`deduct-amount-${bookingId}`} />
          <Input className="w-60" placeholder="What for (or cite a damage report)" value={reason}
            onChange={(e) => setReason(e.target.value)} data-testid={`deduct-reason-${bookingId}`} />
          <Button size="sm" disabled={Number(deduct) > 0 && !reason.trim()}
            onClick={() => act.mutate({ path: `/accommodation/deposits/${d.id}/settle`,
              body: { deductions: Number(deduct) > 0 ? [{ amount: Number(deduct), reason }] : [] } })}
            data-testid={`deposit-settle-${bookingId}`}>
            {Number(deduct) > 0 ? 'Settle with deduction' : 'Room is clean — settle'}
          </Button>
        </div>
      )}
      {d.status === 'settled' && Number(d.refund_due) > 0 && (
        <Button size="sm" variant="outline"
          onClick={() => act.mutate({ path: `/accommodation/deposits/${d.id}/refund`, body: {} })}
          data-testid={`deposit-refund-${bookingId}`}>
          Return {d.currency} {Number(d.refund_due).toFixed(2)}
        </Button>
      )}
      {d.shortfall > 0 && (
        <p className="text-sm text-amber-800" data-testid={`deposit-shortfall-${bookingId}`}>
          The damage exceeds the deposit by {d.currency} {Number(d.shortfall).toFixed(2)} — that is a
          charge to pursue, not a deposit to seize.
        </p>
      )}
    </div>
  );
};

// ==================== ARRIVAL BOARD ====================

const ArrivalBoard = () => {
  const qc = useQueryClient();
  const [day, setDay] = useState('');
  const [keyLabel, setKeyLabel] = useState({});
  const { data, isLoading } = useQuery({
    queryKey: ['arrival-board', day],
    queryFn: () => api(`/accommodation/arrival-board?day=${day}`),
  });
  const act = useMutation({
    mutationFn: ({ path, body }) => api(path, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (_d, vars) => {
      toast.success('Recorded');
      // Clearing the box matters: a label left behind is a second key issued
      // against the same number by whoever presses the button next.
      if (vars.clearKeyFor) setKeyLabel((prev) => ({ ...prev, [vars.clearKeyFor]: '' }));
      qc.invalidateQueries({ queryKey: ['arrival-board'] });
    },
    onError: fail,
  });
  const c = data?.counts || {};

  const KeyIssuer = ({ bookingId }) => (
    <div className="flex gap-2">
      <Input className="w-48" placeholder="Key or card number" value={keyLabel[bookingId] || ''}
        onChange={(e) => setKeyLabel({ ...keyLabel, [bookingId]: e.target.value })}
        data-testid={`key-label-${bookingId}`} />
      <Button size="sm" variant="outline" disabled={!(keyLabel[bookingId] || '').trim()}
        onClick={() => act.mutate({ path: `/accommodation/bookings/${bookingId}/keys`,
          body: { label: keyLabel[bookingId] }, clearKeyFor: bookingId })}
        data-testid={`issue-key-${bookingId}`}>Hand over key</Button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label>Which day</Label><Input type="date" value={day || data?.day || ''} data-testid="board-day"
          onChange={(e) => setDay(e.target.value)} /></div>
        <p className="text-sm text-stone-500 pb-2">Defaults to tomorrow — a board that only shows today cannot be prepared for.</p>
      </div>

      <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[['Arriving', c.arriving], ['Departing', c.departing], ['Rooms not ready', c.rooms_not_ready],
          ['Unpaid arrivals', c.unpaid_arrivals], ['Overdue out', c.overdue_departures], ['Keys out', c.keys_out]
        ].map(([label, value]) => (
          <Card key={label}><CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wide text-stone-500">{label}</p>
            <p className="text-2xl font-semibold text-stone-900">{value ?? '—'}</p>
          </CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">Arriving {data?.day}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {isLoading && <Loader2 className="h-5 w-5 animate-spin text-stone-400" />}
          {(data?.arrivals || []).map((a) => (
            <div key={a.booking_id} className="border border-stone-200 rounded-lg p-4 space-y-2"
              data-testid={`arrival-${a.booking_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-stone-900">{a.name} · Room {a.room_number}</p>
                  <p className="text-sm text-stone-600">
                    {a.occupant_category?.replace('_', ' ')} · {a.nights} night{a.nights === 1 ? '' : 's'}
                    {a.phone ? ` · ${a.phone}` : ''}{a.application_reference ? ` · ${a.application_reference}` : ''}
                  </p>
                </div>
                <div className="flex gap-2">
                  {a.payment_status === 'pending' && <Badge variant="destructive">Unpaid</Badge>}
                  <Badge variant={a.room_ready ? 'secondary' : 'destructive'}>
                    {a.room_ready ? 'Room ready' : `Room ${a.room_status}`}
                  </Badge>
                </div>
              </div>
              {!a.room_ready && (
                <p className="text-sm text-amber-800">
                  This room needs turning around before they arrive.
                </p>
              )}
              <KeyIssuer bookingId={a.booking_id} />
              <DepositPanel bookingId={a.booking_id} mode="arrival" />
            </div>
          ))}
          {!isLoading && !(data?.arrivals || []).length && (
            <p className="text-sm text-stone-500">Nobody is due in on {data?.day}.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Departing {data?.day}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {(data?.departures || []).map((d) => (
            <div key={d.booking_id} className="border border-stone-200 rounded-lg p-4 space-y-2"
              data-testid={`departure-${d.booking_id}`}>
              <p className="font-medium text-stone-900">{d.name} · Room {d.room_number}</p>
              {(d.keys_out || []).length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm text-amber-800">
                    {d.keys_out.length} key(s) still out — now is the moment to ask.
                  </p>
                  {d.keys_out.map((k) => (
                    <div key={k.id} className="flex items-center justify-between gap-2 text-sm">
                      <span className="text-stone-600">{k.kind} {k.label} · out {k.days_out} day(s)</span>
                      <Button size="sm" variant="outline"
                        onClick={() => act.mutate({ path: `/accommodation/keys/${k.id}/return`, body: {} })}
                        data-testid={`return-key-${k.id}`}>Returned</Button>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-stone-500">No keys outstanding.</p>}
              <KeyIssuer bookingId={d.booking_id} />
              <DepositPanel bookingId={d.booking_id} mode="departure" />
            </div>
          ))}
          {!(data?.departures || []).length && (
            <p className="text-sm text-stone-500">Nobody is due out on {data?.day}.</p>
          )}
        </CardContent>
      </Card>

      {(data?.overdue || []).length > 0 && (
        <Card data-testid="overdue-departures">
          <CardHeader>
            <CardTitle className="text-lg">Due out and still here</CardTitle>
            <CardDescription>Quietly, this is how a room gets double-booked.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.overdue.map((o) => (
              <p key={o.booking_id} className="text-sm text-stone-700" data-testid={`overdue-${o.booking_id}`}>
                {o.name} · Room {o.room_number} · was due out {o.due_out} ({o.days_over} day(s) ago)
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      {(data?.keys_out || []).length > 0 && (
        <Card data-testid="keys-out">
          <CardHeader><CardTitle className="text-lg">Every key still out</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.keys_out.map((k) => (
              <div key={k.id} className="flex flex-wrap items-center justify-between gap-2 border border-stone-200 rounded p-3">
                <span className="text-sm text-stone-700">
                  {k.kind} {k.label} · {k.holder} · Room {k.room_number} · out {k.days_out} day(s)
                  {k.days_out > 7 ? ' · chase this' : ''}
                </span>
                <Button size="sm" variant="outline"
                  onClick={() => act.mutate({ path: `/accommodation/keys/${k.id}/return`, body: {} })}
                  data-testid={`return-key-list-${k.id}`}>Returned</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== PAGE ====================

export default function AdminAccommodationOps() {
  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        <header className="space-y-2">
          <a href="/admin/home" className="text-sm text-stone-500 hover:text-stone-900 transition-colors"
            data-testid="back-to-admin">← Admin home</a>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900">Accommodation operations</h1>
          <p className="text-base text-stone-600 max-w-3xl">
            Event accommodation, resident conduct and who may trade from campus. Nothing on this screen cancels a
            booking or removes anybody from a room on its own — each decision is recorded against the officer who took it.
          </p>
        </header>

        <Tabs defaultValue="board">
          <TabsList data-testid="acc-ops-tabs">
            <TabsTrigger value="board" data-testid="tab-board"><Sunrise className="h-4 w-4 mr-2" />Arrival board</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications"><ClipboardList className="h-4 w-4 mr-2" />Applications</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events"><CalendarClock className="h-4 w-4 mr-2" />Event rooms</TabsTrigger>
            <TabsTrigger value="conduct" data-testid="tab-conduct"><ShieldAlert className="h-4 w-4 mr-2" />Conduct</TabsTrigger>
            <TabsTrigger value="restrictions" data-testid="tab-restrictions"><Ban className="h-4 w-4 mr-2" />Restrictions</TabsTrigger>
            <TabsTrigger value="permits" data-testid="tab-permits"><UtensilsCrossed className="h-4 w-4 mr-2" />Food permits</TabsTrigger>
          </TabsList>
          <TabsContent value="board" className="mt-6"><ArrivalBoard /></TabsContent>
          <TabsContent value="applications" className="mt-6"><Applications /></TabsContent>
          <TabsContent value="events" className="mt-6"><EventRooms /></TabsContent>
          <TabsContent value="conduct" className="mt-6"><Conduct /></TabsContent>
          <TabsContent value="restrictions" className="mt-6"><Restrictions /></TabsContent>
          <TabsContent value="permits" className="mt-6"><FoodPermits /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
